import axios, { AxiosHeaders } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { HTTP_METHODS_WITH_BODY, RHZ_API_BASE } from '../config/constants';
import { getCookie } from './UtilityService';

/**
 * Central Axios request interceptor for the OLE client.
 *
 * What this module does:
 * 1) CSRF — Reads the `csrftoken` cookie and sets `x-CSRFToken` on every outbound request when the cookie
 *    is present (Django-style). Applies to all URLs, not only RHZ.
 * 2) Payload size — Sets axios default max body/content length to Infinity so large course/report payloads
 *    are not rejected by the client.
 * 3) RHZ `/rhz/api/` write requests — For POST, PUT, PATCH only: if the body is empty (undefined, null, or '')
 *    and the request does not look like a multipart or urlencoded form upload, the body is set to `{}`.
 *    This keeps legacy RHZ + Axios 1.x behavior predictable. Real file/form uploads are not touched.
 *
 */

// Allow large request/response payloads used by course and report endpoints.
axios.defaults.maxContentLength = Infinity;
axios.defaults.maxBodyLength = Infinity;

// Join baseURL + url the same way axios will for the final request (url may be relative).
const buildFullUrl = (
  config: Pick<InternalAxiosRequestConfig, 'baseURL' | 'url'>,
): string => {
  const path = typeof config.url === 'string' ? config.url : '';
  return `${config.baseURL ?? ''}${path}`;
};

// True when the resolved URL contains the RHZ API prefix (see `RHZ_API_BASE` in config/constants.js).
const isJsonOnlyApiRoute = (config: InternalAxiosRequestConfig): boolean =>
  buildFullUrl(config).includes(RHZ_API_BASE);

// Axios may send `Content-Type` or `content-type`; normalize for substring checks below.
const readContentTypeLowercase = (headers: AxiosHeaders): string => {
  const raw = headers.get('Content-Type') ?? headers.get('content-type') ?? '';
  return String(raw).toLowerCase();
};

/* eslint-disable no-param-reassign -- Axios mutates the shared request config in place */
// Request pipeline (order matters):
// 1) Normalize headers to AxiosHeaders so `get` / `set` work reliably.
// 2) Attach CSRF when csrftoken cookie exists (including null vs undefined: only skip for null/undefined).
// 3) For RHZ API routes with a body method, optionally coerce empty non-form bodies to `{}`.
const onRequest = (config: InternalAxiosRequestConfig) => {
  // Ensure headers support AxiosHeaders APIs (`get`, `set`) consistently.
  config.headers = AxiosHeaders.from(config.headers ?? {});

  // CSRF token from cookie is attached to every outbound request when present.
  const csrfToken = getCookie('csrftoken');
  if (csrfToken != null) {
    config.headers.set('x-CSRFToken', csrfToken);
  }

  // Non-RHZ routes: no further body normalization.
  if (!isJsonOnlyApiRoute(config)) {
    return config;
  }

  // Only POST, PUT, PATCH carry bodies we normalize (see HTTP_METHODS_WITH_BODY in constants).
  const method = String(config.method ?? 'get').toLowerCase();
  if (!HTTP_METHODS_WITH_BODY.includes(method)) {
    return config;
  }

  const body = config.data;
  const contentType = readContentTypeLowercase(config.headers);

  const bodyIsEmpty = body === undefined || body === null || body === '';
  // Do not replace body for real uploads or classic form posts (empty body may be intentional there).
  const looksLikeFileOrFormUpload =
    contentType.includes('multipart/form-data') ||
    contentType.includes('application/x-www-form-urlencoded');

  if (bodyIsEmpty && !looksLikeFileOrFormUpload) {
    // Legacy RHZ + Axios 1: send `{}` for empty JSON-style writes so servers still receive a JSON object.
    config.data = {};
  }

  return config;
};
/* eslint-enable no-param-reassign */

// Register on the default axios instance used across services.
axios.interceptors.request.use(onRequest, (err: unknown) =>
  Promise.reject(err),
);

// No exports: importing this file runs the registration above. TypeScript needs a module marker.
export {};
